import React, { useEffect, useRef, useState } from "react";

interface IframeSectionProps {
    flkState: any;
}

/** How long to wait for the signing page before offering a way out. */
const LOAD_TIMEOUT_MS = 15000;

/**
 * Hosts the FLK signing page.
 *
 * The iframe used to be rendered bare — no load state, no error state, no title.
 * When FLK was slow, refused to frame, or the link had expired, the candidate saw
 * an empty box with nothing to tell them whether to wait or act.
 */
const IframeSection: React.FC<IframeSectionProps> = ({ flkState }) => {
    const signingUrl = flkState.signingUrl;
    const type = flkState.type;

    const [loaded, setLoaded] = useState(false);
    const [timedOut, setTimedOut] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setLoaded(false);
        setTimedOut(false);

        const cleanup = () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };

        if (!signingUrl) { return cleanup; }

        timerRef.current = setTimeout(() => {
            setTimedOut(true);
        }, LOAD_TIMEOUT_MS);

        return cleanup;
    }, [signingUrl]);

    const handleLoad = () => {
        setLoaded(true);
        setTimedOut(false);
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    };

    if (!signingUrl || !type) { return null; }

    return (
        <div className="signed_url_wrapper">
            {!loaded && !timedOut && (
                <div className="alert alert-info" role="status" aria-live="polite">
                    Loading your document for signing&hellip;
                </div>
            )}

            {timedOut && !loaded && (
                <div className="alert alert-warning" role="alert">
                    Your document is taking longer than usual to load.{" "}
                    <a href={signingUrl} target="_blank" rel="noopener noreferrer">
                        Open it in a new tab
                    </a>{" "}
                    to sign it, then come back to this page and press continue.
                </div>
            )}

            <iframe
                className="esign"
                src={signingUrl}
                title="Sign your document"
                onLoad={handleLoad}
            ></iframe>
        </div>
    );
};

export default IframeSection;
